import { Inject, InjectionToken, OnInit, OnDestroy, AfterViewInit, HostListener, PLATFORM_ID, Renderer2, ElementRef, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router, NavigationEnd, GuardsCheckEnd } from '@angular/router';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Subscription, timer } from 'rxjs';
import { GENERATOR_ENV_TOKEN, GeneratorEnv } from '../generator-env-token';
import { TemplateService } from './service/template.service';
import { Menu } from './entity/menus';
import { ServerConfigService } from 'src/app/share/service/server-config.service';
import { loadLazyJsFile, removeUrlQueryParam } from 'src/app/util';
import { ISiteInfo } from './entity/siteInfo';
import { BackStageTemplateService, TemplateType } from 'src/app/share/service/back-stage-template.service';
import { EventReplayer } from 'preboot';
import { TmModalComponent } from '@techmore/global';

export const ABSTRACT_TEMPLATE_TOKEN = new InjectionToken<AbstractTemplate>('abstract template component') ;

export abstract class AbstractTemplate implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('bulletinModal', {static: false}) bulletinModal: TmModalComponent;
  /** 頁面頂端連結 */
  @ViewChild('pageTopAnchor', {static: false}) pageTopAnchorRef: ElementRef;
  get pageTopAnchor() { if (this.pageTopAnchorRef) { return this.pageTopAnchorRef.nativeElement as HTMLElement; } else { return null; } }
  /** 頁面頂端連結 */
  @ViewChild('contentAnchor', {static: false}) contentAnchorRef: ElementRef;
  get contentAnchor() { if (this.contentAnchorRef) { return this.contentAnchorRef.nativeElement as HTMLElement; } else { return null; } }
  /** 是否為瀏覽器端渲染 */
  isBrowser: boolean;
  /** 是否為編輯狀態 */
  editable: boolean;
  /** 是否為編輯首頁狀態 */
  isEditIndex: boolean;
  /** 是否在首頁 */
  isIndex: boolean;
  /** 目前使用的語系 */
  currentLang: string;
  /** 網站基本資料 */
  siteInfo: ISiteInfo;
  /** 網站設定 nar 的資料 */
  get narBar() {
    return this.siteInfo && this.siteInfo.narBar || null;
  }
  /** 網站設定的資料 */
  get metadata() {
    return this.siteInfo && this.siteInfo.metadata || null;
  }
  /** 首頁 logo 圖檔 */
  get logoImageFile() {
    return this.siteInfo && this.siteInfo.metadata && this.siteInfo.metadata.logoFile && this.siteInfo.metadata.logoFile[0] || null;
  }
  /** 主選單 */
  menus: Menu[];
  /** 頁腳 */
  get footer() {
    return this.siteInfo && this.siteInfo.footer || null;
  }
  /** api 取回的網站動態選單地圖 */
  siteMap: Array<Menu>;
  /** 顯示在頁腳用的網站動態選單地圖 */
  footerSiteMap: Array<Menu>;
  /** 網站擁有的語系 */
  langs: Array<string> = [];
  /** 顯示返回頂部按鈕 */
  showGoTop = false;
  /** 是否顯示胖胖頁腳 */
  showFooter = false;
  private _siteInfoSubscription: Subscription;
  private _routerNavEndSubscription: Subscription;
  private _breadCrumbsSubscription: Subscription;

  constructor(
    @Inject(GENERATOR_ENV_TOKEN) protected generatorEnv: GeneratorEnv,
    @Inject(TemplateService) protected templateService: TemplateService,
    @Inject(Router) protected router: Router,
    @Inject(ActivatedRoute) protected route: ActivatedRoute,
    @Inject(DOCUMENT) protected document: Document,
    @Inject(TranslateService) protected translate: TranslateService,
    @Inject(PLATFORM_ID) protected platformId: object,
    @Inject(Renderer2) protected render: Renderer2,
    @Inject(ElementRef) protected el: ElementRef,
    @Inject(ServerConfigService) protected serverConfigService: ServerConfigService,
    @Inject(BackStageTemplateService) protected backStageTemplateService: BackStageTemplateService,
    @Inject(EventReplayer) protected replayer: EventReplayer,
  ) {
    // tslint:disable-next-line: no-string-literal
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.currentLang = this.translate.currentLang;
    if ((this.serverConfigService.isSSRMode && !this.isBrowser) || (!this.serverConfigService.isSSRMode && this.isBrowser)) {
      // 設定 SSR html tag 的 lang
      if (this.currentLang === 'zh-tw') {
        this.setHtmlLang('zh-Hant');
      } else if (this.currentLang === 'en-us') {
        // 無障礙 將英文語系 us > en
        this.setHtmlLang('en');
      }
    }
    // 編輯狀態
    this.editable = this.serverConfigService.isEditModeUrl;
    this.isEditIndex = this.backStageTemplateService.type === TemplateType.index;
    // 首頁狀態
    if (this.editable) {
      const url = removeUrlQueryParam(this.router.url);
      this.isIndex = url === '/' + this.translate.currentLang;
    } else {
      this.isIndex = this.router.url === '/' + this.translate.currentLang;
    }
  }

  ngOnInit(): void {
    // menu
    this._siteInfoSubscription = this.templateService.siteInfo$.subscribe(siteInfo => {
      this.siteInfo = siteInfo;

      this.menus = this.genMainMenu(siteInfo.menus);
      this.footerSiteMap = siteInfo.menus;
      this.showFooter = siteInfo.template.showFooter && !0;

      // lang FIXME: 後端要怎麼給我語系?
      // this.langs = this.serverConfigService.getLangs(this.route.snapshot.params['path']);
      this.langs = ['zh-TW'];
      this.googleTranslateElementInit();
    });

    let isUrlPathChange = false;
    this._routerNavEndSubscription = this.router.events.subscribe((event: NavigationEnd) => {
      if (event instanceof GuardsCheckEnd) {
        const fromUrl = removeUrlQueryParam(this.router.url);
        const targetUrl = removeUrlQueryParam(event.url);
        if (fromUrl !== targetUrl) {
          isUrlPathChange = true;
        } else {
          isUrlPathChange = false;
        }
      }

      if (!(event instanceof NavigationEnd)) { return; }

      if (isUrlPathChange) {
        // 首頁狀態
        if (this.editable) {
          this.isIndex = '/' + this.router.url.split('/').splice(2).join('/') === '/' + this.translate.currentLang;
        } else {
          if (this.generatorEnv.mode === 'path') {
            this.isIndex = (this.router.url.split('/').length === 3);
          } else if (this.generatorEnv.mode === 'domain') {
            this.isIndex = this.router.url === '/' + this.translate.currentLang;
          }
        }

        // 無障礙 自動聚焦頁面頂端鏈結
        if (this.pageTopAnchor) {
          this.pageTopAnchor.focus();
        }

        if (this.isIndex) {
          this.templateService.setWebTitle({title: '', desc: '', image: ''});
        }
      }
    });
  }

  ngOnDestroy() {
    if (this._siteInfoSubscription) {
      this._siteInfoSubscription.unsubscribe();
    }
    if (this._routerNavEndSubscription) {
      this._routerNavEndSubscription.unsubscribe();
    }
    if (this._breadCrumbsSubscription) {
      this._breadCrumbsSubscription.unsubscribe();
    }
  }

  ngAfterViewInit() {
    // 無障礙 自動聚焦頁面頂端鏈結
    this.pageTopAnchor.focus();
    if (this.isBrowser && !this.editable) {
      // TODO: 待改進
      timer(1000).subscribe(t => {
        this.replayer.replayAll();
      });

      // if (!localStorage.getItem('isShowBulletin') || localStorage.getItem('isShowBulletin') !== 'close') {
      //   this.bulletinModal.open();
      // }
    }
  }

  /** 設定 SSR html tag 的 lang */
  protected setHtmlLang(lang: string) {
    // const html = this.el.nativeElement.closest('html');
    // this.document.documentElement.lang = lang;
    this.render.setAttribute(this.document.documentElement, 'lang', lang);
  }

  /** 焦點移動到中間內容區塊 */
  focusContentKey($event?: UIEvent) {
    if (!$event || ($event instanceof KeyboardEvent && $event.key === 'Enter') || $event instanceof MouseEvent) {
      this.contentAnchor.focus();
    }
  }

  @HostListener('window:scroll', ['$event'])
  onScrollEvent($event) {
    this.setBackTopAnchorVisible();
  }

  /** 設定返回上面按鈕是否可視 */
  private setBackTopAnchorVisible() {
    if (document.documentElement.scrollTop > document.body.scrollHeight / 3) {
      this.showGoTop = true;
    } else {
      this.showGoTop = false;
    }
  }

  /** 返回頁面頂端 */
  setFocusTop() {
    window.scrollTo(0, 0);
    this.pageTopAnchor.focus();
  }

  /** 產生主選單的資料結構 */
  genMainMenu(menus: Menu[]) {
    const mainMenus: Menu[] = [];

    menus.forEach(menu => {
      if (menu.menuStatus) {
        const clone = {...menu};
        if (clone.menus) {
          clone.menus = this.genMainMenu(clone.menus);
        }
        mainMenus.push(clone);
      }
    });

    return mainMenus;
  }

  // 取得 googleTranslate要顯示的語言
  googleTranslateElementInit() {
    let gettemp = 'zh-TW';
    let viewGoogleTranslate = false;
    // 1-5

    if (this.narBar['English'] === true) {
       gettemp += ',en';
       this.langs.push('en');
       viewGoogleTranslate = true;
    }

    /** 韓文 */
    if (this.narBar['Korean'] === true) {
       gettemp += ',ko';
       this.langs.push('ko');
       viewGoogleTranslate = true;
    }
    /** 日文 */
    if (this.narBar['Japanese'] === true) {
      gettemp += ',ja';
      this.langs.push('ja');
      viewGoogleTranslate = true;
    }
    /** 泰文 */
    if (this.narBar['Thai'] === true) {
      gettemp += ',th';
      this.langs.push('th');
      viewGoogleTranslate = true;
    }
    /** 越南語 */
    if (this.narBar['Vietnamese'] === true) {
      gettemp += ',vi';
      this.langs.push('vi');
      viewGoogleTranslate = true;
    }
    /** 菲律賓語 */
    if (this.narBar['Filipino'] === true) {
      gettemp += ',tl';
      this.langs.push('tl');
      viewGoogleTranslate = true;
    }
    // 6-10
    /** 印尼 */
    if (this.narBar['Indonesian'] === true) {
      gettemp += ',id';
      this.langs.push('id');
      viewGoogleTranslate = true;
    }
    /** 馬來語 */
    if (this.narBar['Malay'] === true) {
      gettemp += ',ms';
      this.langs.push('ms');
      viewGoogleTranslate = true;
    }
    /** 印地語 */
    if (this.narBar['Hindi'] === true) {
      gettemp += ',hi';
      this.langs.push('hi');
      viewGoogleTranslate = true;
    }
    /** 西班牙語 */
    if (this.narBar['Spanish'] === true) {
      gettemp += ',es';
      this.langs.push('es');
      viewGoogleTranslate = true;
    }
    /** 法語 */
    if (this.narBar['French'] === true) {
      gettemp += ',fr';
      this.langs.push('fr');
      viewGoogleTranslate = true;
    }
    // 11-16
    /** 德文 */
    if (this.narBar['German'] === true) {
      gettemp += ',de';
      this.langs.push('de');
      viewGoogleTranslate = true;
    }
    /** 葡萄牙語 */
    if (this.narBar['Portuguese'] === true) {
      gettemp += ',pt';
      this.langs.push('pt');
      viewGoogleTranslate = true;
    }
    /** 荷蘭語 */
    if (this.narBar['Dutch'] === true) {
      gettemp += ',nl';
      this.langs.push('nl');
      viewGoogleTranslate = true;
    }
    /** 義大利語 */
    if (this.narBar['Italian'] === true) {
      gettemp += ',it';
      this.langs.push('it');
      viewGoogleTranslate = true;
    }
    /** 俄文 */
    if (this.narBar['Russian'] === true) {
      gettemp += ',ru';
      this.langs.push('ru');
      viewGoogleTranslate = true;
    }
    /** 阿拉伯 */
    if (this.narBar['Arabic'] === true) {
      gettemp += ',ar';
      this.langs.push('ar');
      viewGoogleTranslate = true;
    }
    if (viewGoogleTranslate === true ) {
      if (this.isBrowser) {
        // let v = document.createElement('script');
        // v.type = 'text/javascript';
        // const temp1 = 'function() { new google.translate.TranslateElement({ pageLanguage: \'zh\' ,includedLanguages:\'';
        // const temp3 = '\',layout:google.translate.TranslateElement.InlineLayout.SIMPLE}, \'google_translate_element\'); } ';
        // const temp = temp1 + gettemp + temp3;
        // v.innerHTML = temp;
        // this.el.nativeElement.appendChild(v);

        // tslint:disable-next-line: only-arrow-functions
        window['googleTranslateElementInit'] = function() {
          // tslint:disable-next-line: no-unused-expression
          new window['google']['translate'].TranslateElement({
            pageLanguage: 'zh',
            includedLanguages: gettemp,
            layout: window['google']['translate'].TranslateElement.InlineLayout.VERTICAL
          }, 'google_translate_element');
        };
      }

      loadLazyJsFile(this.render, 'translateGoogle', '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit', true);
    }
  }

  goTranslate(lang: string) {
    $('.goog-te-combo').val(lang);

    const triggerEvent = (element: any, eventName: any) => {
        const event = new Event(eventName);
        element.dispatchEvent(event);
    };
    triggerEvent(document.querySelector('.goog-te-combo'), 'change');
  }

  closeBulletin() {
    this.bulletinModal.close();
    localStorage.setItem('isShowBulletin', 'close');
  }
}
